/** The quick actions every value on a background-task surface offers — the
 *  task details dialog, and the two review drawers that hang off a paused
 *  task (aux-sync records, AI-import drafts). One module because all three
 *  read the SAME record: a `BackgroundTaskRecord` with a tenant, an
 *  integration and a bag of arguments.
 *
 *  `/bgtasks` is a plain-param grid, so its links are `bgtasksHref({...})`.
 *
 *  Arguments are the interesting part. A task's `params` is free-form JSON
 *  written by whichever launcher started it, so the linker is an ALLOW LIST,
 *  never a heuristic: a key earns a link only when its id space is known.
 *  The trap is `ids` — batch tasks carry console pks, sync tasks carry the
 *  vendor's external ids, and the two look identical. Unless the task itself
 *  says `resource: 'bookings'`, `ids` gets the custom-id search
 *  (`/bookings?q=id=`), never `/bookings/<pk>`.
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  auditHref,
  bgtasksHref,
  bookingsHref,
  changesHref,
  cond,
  extIdsHref,
  idCond,
  toursHref,
  usersHref,
} from '@/utils/gridLinks';

export interface TaskCtx {
  /** The task's tenant — most argument links are only resolvable inside it. */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: TaskCtx) => ctx.tenantId ?? null;

// ---- Task fields ----------------------------------------------------------

export function stateActions(state: number, label: string): ValueAction[] {
  return [act.filter(bgtasksHref({ state }), `Tasks "${label}"`)];
}

export function taskTypeActions(taskType: string): ValueAction[] {
  return [
    act.copy(taskType, 'Copy task type'),
    act.filter(bgtasksHref({ task_type: taskType }), `Every "${taskType}" run`),
  ];
}

export function visibilityActions(visibility: number, label: string): ValueAction[] {
  return [act.filter(bgtasksHref({ visibility }), `Tasks visible to "${label}"`)];
}

export function tenantActions(tenant: { id: number }): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(bgtasksHref({ tour_operator_id: tenant.id }), 'Tasks of this operator'),
  ];
}

/** Console-scoped tasks (no tenant) — the grid's `tour_operator_id=null`. */
export function consoleTenantActions(): ValueAction[] {
  return [act.filter(bgtasksHref({ tour_operator_id: 'null' }), 'Console-scoped tasks')];
}

/** Who launched it. `/bgtasks?user=` matches the email (Phase 8 widens it to
 *  the id), and the audit log tells the rest of that person's story. */
export function initiatedByActions(user: { id: number | null; email: string | null }): ValueAction[] {
  const out: ValueAction[] = [];
  if (user.id != null) out.push(act.open(`/users/${user.id}`, 'Open user'));
  if (user.email) {
    out.push(
      act.filter(bgtasksHref({ user: user.email }), 'Tasks launched by this user'),
      act.filter(auditHref({ actor_email: user.email }), 'Audit trail of this actor'),
      act.mailto(user.email),
    );
  }
  return out;
}

export function taskIdActions(id: string): ValueAction[] {
  return [
    act.copy(id, 'Copy task id'),
    act.filter(changesHref({ task_id: id }), 'Records this run changed'),
    act.filter(auditHref({ task_id: id }), "This run's audit trail"),
  ];
}

/** An artifact's S3 key — the object ops paste into the bucket console. */
export function artifactActions(artifact: { name: string; s3_key: string }): ValueAction[] {
  return [act.copy(artifact.s3_key, 'Copy S3 key'), act.copy(artifact.name, 'Copy file name')];
}

// ---- Shared references (task dialog + review drawers) ---------------------

/** An integration codename on a task or a review record. */
export function integrationActions(codename: string, ctx: TaskCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [act.copy(codename, 'Copy integration')];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

/** A restech ACCOUNT slug on a review record. */
export function accountActions(slug: string, ctx: TaskCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [act.copy(slug, 'Copy account slug')];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

/** A config-target record: the operator's Configuration tab owns it. */
export function configTargetActions(ctx: TaskCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return t != null ? [act.open(`/tour-operators/${t}/configuration`, "Operator's configuration")] : [];
}

/** A vendor product id: the mapping that resolves it, and the tour it names. */
export function productIdActions(productId: string, ctx: TaskCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(productId, 'Copy product id'),
    act.filter(extIdsHref([cond('value', productId)], t), 'External-ID mappings for this product'),
    act.filter(toursHref([cond('custom_id', productId)], t), 'Tours with this custom id'),
  ];
}

export function tourActions(tourId: number): ValueAction[] {
  return [
    act.open(`/tours/${tourId}`, 'Open tour'),
    act.open(`/tours/${tourId}#mapping`, "Tour's identifiers"),
  ];
}

/** An aux-sync receipt's created/updated tag. */
export function receiptTagActions(tagId: number, ctx: TaskCtx): ValueAction[] {
  return [
    act.open(`/tags/${tagId}`, 'Open tag'),
    act.filter(bookingsHref([idCond('tag', tagId)], tenantOf(ctx)), 'Bookings with this tag'),
  ];
}

/** Background tasks this task launched — a product sync's booking moves
 *  (`booking_move_task_ids` in its result, plan 2026-10-11 product-sync joins
 *  §5a). Capped like every list argument. */
export function launchedTaskActions(ids: unknown): ValueAction[] {
  return listOf(ids)
    .slice(0, MAX_LIST_LINKS)
    .map((id) => act.open(`/bgtasks/${id}`, `Open task ${id}`));
}

// ---- The arguments linker (allow list) ------------------------------------

/** Keys whose value is a single console record id in a KNOWN id space. */
const SCALAR_LINKERS: Record<string, (value: string, ctx: TaskCtx) => ValueAction[]> = {
  tour_operator_id: (v) => [
    act.open(`/tour-operators/${v}`, 'Open operator'),
    act.filter(bgtasksHref({ tour_operator_id: v }), 'Tasks of this operator'),
  ],
  import_run_id: (v) => [act.open(`/import-runs/${v}/diagnose`, 'Open import run')],
  api_request_id: (v) => [act.open(`/api-requests/${v}/diagnose`, 'Open API request')],
  integration_name: (v, ctx) => integrationActions(v, ctx),
  // A task another task started (a product sync's booking move): back to it.
  launched_by_task_id: (v) => [act.open(`/bgtasks/${v}`, 'Open the task that launched it')],
};

/** How many entries of a list argument get their own actions. */
const MAX_LIST_LINKS = 5;

/** The console resources whose batch `ids` ARE console booking pks. */
const BOOKING_PK_RESOURCES = new Set(['bookings']);

function listOf(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
}

/**
 * Actions for one `params` entry, or `[]` when the key is not allow-listed.
 *
 * `params` is the whole argument bag, because two keys need their siblings:
 * `ids` needs `resource` to know whether it holds console pks, and the
 * integration/account keys need the task's tenant (carried in `ctx`).
 */
export function taskParamActions(
  key: string,
  value: unknown,
  params: Record<string, unknown>,
  ctx: TaskCtx,
): ValueAction[] {
  const scalar = SCALAR_LINKERS[key];
  if (scalar && value != null && typeof value !== 'object') {
    const text = String(value);
    return text ? scalar(text, ctx) : [];
  }

  if (key === 'user_emails') {
    return listOf(value)
      .slice(0, MAX_LIST_LINKS)
      .flatMap((email) => [
        act.filter(
          usersHref('guests', { tenant: tenantOf(ctx), email_contains: email }),
          `Guest accounts for ${email}`,
        ),
        act.mailto(email),
      ]);
  }

  if (key === 'booking_move_task_ids') return launchedTaskActions(value);

  if (key === 'ids') {
    const resource = typeof params.resource === 'string' ? params.resource : null;
    const ids = listOf(value).slice(0, MAX_LIST_LINKS);
    if (resource && BOOKING_PK_RESOURCES.has(resource)) {
      return ids.flatMap((id) => [act.open(`/bookings/${id}`, `Open booking ${id}`)]);
    }
    // Unknown id space: offer the custom-id search, never a pk route.
    return ids.map((id) =>
      act.filter(bookingsHref([cond('id', id)], tenantOf(ctx)), `Bookings with custom id ${id}`),
    );
  }

  return [];
}
